import {
  useLazyQuery,
  useSubscription,
  useSuspenseQuery,
} from '@apollo/client';
import { RumInitConfiguration, datadogRum } from '@datadog/browser-rum';
import React, { useEffect, useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useMount } from 'react-use';

import { FCC } from '@eluve/components';
import {
  LoggerArgs,
  LoggingContextProvider,
  useExtendedLogger,
} from '@eluve/logger';
import { useUserIdFromSession } from '@eluve/session-helpers';
import {
  getCoreTenantDataQuery,
  listenTenantFeatureFlagsSubscription,
} from '@eluve/shared-gql-operations';
import { useEluveExtExists, useExternalEhrs } from '@eluve/smart-blocks';
import {
  DbProvider,
  UserFileSystemInitializer,
  UserFileSystemStoreProvider,
} from '@eluve/user-local-files';
import { useTenantLocalStorage } from '@eluve/utility-hooks';

import { appConfig } from '../../../../config';
import { initPostMessenger } from '../../utils/post-messenger';

const CORE_TENANT_POLL_INTERVAL = 1000 * 60 * 15; // 15 minutes

export const TenantProvider: FCC = ({ children }) => {
  const { tenantId } = useParams() as { tenantId: string };

  const tenantLoggerArgs: LoggerArgs = useMemo(
    () => ({ name: 'TenantScoped', metadata: { tenantId } }),
    [tenantId],
  );
  const tenantLogger = useExtendedLogger(tenantLoggerArgs);

  const userId = useUserIdFromSession();
  const externalEhrs = useExternalEhrs(tenantId);
  const { eluveExtExists } = useEluveExtExists();

  const tenantData = useSuspenseQuery(getCoreTenantDataQuery, {
    variables: {
      tenantId,
    },
  });

  const [_, pollingQuery] = useLazyQuery(getCoreTenantDataQuery, {
    variables: {
      tenantId,
    },
    // Serve the first one from cache only since we already retrieved this
    // data using suspense above. After that we can refetch on the polling interval
    initialFetchPolicy: 'cache-only',
  });

  useMount(() => {
    pollingQuery.startPolling(CORE_TENANT_POLL_INTERVAL);
  });

  useSubscription(listenTenantFeatureFlagsSubscription, {
    variables: {
      tenantId: tenantId,
    },
  });

  useEffect(() => {
    if (
      eluveExtExists &&
      externalEhrs?.length &&
      tenantData?.data?.tenantsByPk
    ) {
      initPostMessenger(
        externalEhrs.map(({ domain, vendor }) => ({
          domain,
          vendor,
        })),
        tenantId,
        tenantData.data.tenantsByPk.name,
      );
    }
  }, [eluveExtExists, externalEhrs, tenantData, tenantId]);

  const [advancedMonitoring] = useTenantLocalStorage({
    tenantId,
    key: 'ADVANCED_MONITORING',
    initialValue: false,
  });

  useEffect(
    function updateDatadogRumUserDetails() {
      (async () => {
        if (userId && tenantId) {
          let trackingConsent: RumInitConfiguration['trackingConsent'] =
            'not-granted';
          const isSiloamProd = appConfig.VITE_API_DOMAIN.includes('siloam');
          const isStaging =
            appConfig.VITE_METRICS_ENV.includes('staging') ||
            appConfig.VITE_METRICS_ENV.includes('eluve-sargis');
          if (isSiloamProd || isStaging) {
            trackingConsent = 'granted';
          } else {
            trackingConsent = advancedMonitoring ? 'granted' : 'not-granted';
          }

          datadogRum.setTrackingConsent(trackingConsent);
          datadogRum.setUser({
            id: userId,
            tenantId,
          });
        }
      })();
    },
    [userId, tenantId, advancedMonitoring],
  );

  if (tenantData.data.tenantsByPk?.isPendingApproval) {
    return <Navigate to={`/organizations/pending-approval`} />;
  }

  return (
    <UserFileSystemStoreProvider
      initializer={() => ({
        userId,
        tenantId,
        userFileSystem: {
          type: 'pending',
        },
      })}
    >
      <LoggingContextProvider loggerInput={tenantLogger}>
        <DbProvider userId={userId} tenantId={tenantId}>
          <UserFileSystemInitializer>{children}</UserFileSystemInitializer>
        </DbProvider>
      </LoggingContextProvider>
    </UserFileSystemStoreProvider>
  );
};
